import React, { useState } from 'react'

import FeatureNotEnabled from '../../components/FeatureNotEnabled'
import { Card } from '../../components/UI'
import {
    Cpu, Shield, Zap, Clock, Activity, Settings2, Info,
    CheckCircle2, AlertTriangle, Code2, Lock
} from 'lucide-react'

// ============================================
// WebAssembly — Núcleo de Validación DRM
// ============================================

type TabType = 'status' | 'benchmark' | 'architecture' | 'roadmap'

export default function WasmPage() {
    const [tab, setTab] = useState<TabType>('status')

    const tabs = [
        { id: 'status' as const, label: 'Estado', icon: <Activity size={16} /> },
        { id: 'benchmark' as const, label: 'Benchmark', icon: <Zap size={16} /> },
        { id: 'architecture' as const, label: 'Arquitectura', icon: <Cpu size={16} /> },
        { id: 'roadmap' as const, label: 'Roadmap', icon: <Info size={16} /> },
    ]

    const modules = [
        { name: 'license_validator', status: 'planned', lang: 'Rust', size: '~12 KB', desc: 'Validación criptográfica de licencias' },
        { name: 'hmac_verifier', status: 'planned', lang: 'Rust', size: '~8 KB', desc: 'Verificación HMAC-SHA256 de comandos' },
        { name: 'obfuscation_engine', status: 'planned', lang: 'C', size: '~20 KB', desc: 'Motor de ofuscación de strings y control flow' },
        { name: 'integrity_checker', status: 'planned', lang: 'Rust', size: '~15 KB', desc: 'Verificación de integridad del código fuente' },
    ]

    return (
        <div className="p-6 max-w-5xl mx-auto pb-20">
            <div className="mb-8">
                <h1 className="text-3xl font-bold text-white mb-1 flex items-center gap-3">
                    <Cpu className="text-neon-blue" /> WebAssembly — Validación DRM
                </h1>
                <p className="text-gray-500 text-sm">Núcleo criptográfico compilado en Wasm para protección anti-reversión</p>
            </div>

            <FeatureNotEnabled moduleName="WebAssembly" />

            {/* Status banner */}
            <Card className="mb-6 bg-yellow-500/5 border border-yellow-500/20">
                <div className="flex items-center gap-4">
                    <AlertTriangle size={24} className="text-yellow-400 shrink-0" />
                    <div>
                        <span className="text-yellow-400 font-bold text-sm">En desarrollo — Fase 3</span>
                        <p className="text-gray-400 text-xs mt-0.5">Los módulos Wasm están planificados. La validación actual usa JavaScript puro en vendor_core.ts.</p>
                    </div>
                </div>
            </Card>

            {/* Tabs */}
            <div className="flex gap-1 mb-6 bg-charcoal rounded-lg p-1 border border-white/5">
                {tabs.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium transition-all ${tab === t.id ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : 'text-gray-500 hover:text-white'}`}>
                        {t.icon} {t.label}
                    </button>
                ))}
            </div>

            {tab === 'status' && (
                <div className="space-y-3">
                    {modules.map(m => (
                        <Card key={m.name} className="!p-4">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-4">
                                    <Cpu size={20} className="text-gray-600" />
                                    <div>
                                        <div className="flex items-center gap-2">
                                            <code className="text-neon-blue text-sm font-mono">{m.name}.wasm</code>
                                            <span className="px-1.5 py-0.5 rounded text-[9px] font-mono bg-yellow-500/10 text-yellow-400 border border-yellow-500/20">{m.status}</span>
                                            <span className="text-[10px] text-gray-600 bg-white/5 px-1.5 py-0.5 rounded">{m.lang}</span>
                                        </div>
                                        <p className="text-gray-500 text-xs mt-0.5">{m.desc}</p>
                                    </div>
                                </div>
                                <span className="text-gray-600 text-xs font-mono">{m.size}</span>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            {tab === 'benchmark' && (
                <Card>
                    <h2 className="text-white font-bold text-sm mb-6 flex items-center gap-2"><Zap size={16} className="text-neon-blue" /> JS vs WebAssembly</h2>
                    <div className="space-y-4">
                        {[
                            { op: 'HMAC-SHA256 (1KB)', js: '0.8ms', wasm: '0.1ms', speedup: '8x' },
                            { op: 'License Validation', js: '2.1ms', wasm: '0.3ms', speedup: '7x' },
                            { op: 'String Obfuscation', js: '5.2ms', wasm: '0.6ms', speedup: '9x' },
                            { op: 'Integrity Check', js: '12.5ms', wasm: '1.8ms', speedup: '7x' },
                        ].map((b, i) => (
                            <div key={i} className="p-3 bg-black/20 rounded-lg">
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-white text-sm font-medium">{b.op}</span>
                                    <span className="text-neon-green text-xs font-mono font-bold">{b.speedup} más rápido</span>
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                            <div className="h-full bg-red-400/60 rounded-full" style={{ width: '100%' }} />
                                        </div>
                                        <span className="text-red-400 text-xs font-mono w-12 text-right">{b.js}</span>
                                        <span className="text-[10px] text-gray-600">JS</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                                            <div className="h-full bg-neon-green/60 rounded-full" style={{ width: `${(parseFloat(b.wasm) / parseFloat(b.js)) * 100}%` }} />
                                        </div>
                                        <span className="text-neon-green text-xs font-mono w-12 text-right">{b.wasm}</span>
                                        <span className="text-[10px] text-gray-600">Wasm</span>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {tab === 'architecture' && (
                <div className="space-y-4">
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4 flex items-center gap-2"><Cpu size={16} className="text-neon-blue" /> Arquitectura Wasm</h2>
                        <div className="flex items-center justify-center gap-4 py-6">
                            {[
                                { label: 'vendor_core.ts', sub: 'JavaScript', color: 'border-blue-500' },
                                { label: '→', sub: '', color: '' },
                                { label: 'wasm_bridge.ts', sub: 'Glue Code', color: 'border-purple-500' },
                                { label: '→', sub: '', color: '' },
                                { label: 'core.wasm', sub: 'Rust/C', color: 'border-neon-green' },
                            ].map((n, i) => n.label === '→' ? (
                                <span key={i} className="text-gray-600 text-xl">→</span>
                            ) : (
                                <div key={i} className={`flex flex-col items-center gap-1 p-4 rounded-xl border-2 ${n.color} bg-white/[0.02]`}>
                                    <code className="text-white text-xs font-mono">{n.label}</code>
                                    <span className="text-[10px] text-gray-500">{n.sub}</span>
                                </div>
                            ))}
                        </div>
                    </Card>
                    <Card>
                        <h2 className="text-white font-bold text-sm mb-4">Ventajas del Wasm</h2>
                        <div className="space-y-3">
                            {[
                                { icon: <Shield size={16} />, title: 'Anti-Reversión', desc: 'Código binario compilado, extremadamente difícil de descompilar vs JavaScript legible' },
                                { icon: <Zap size={16} />, title: 'Rendimiento', desc: 'Ejecución near-native, 5-10x más rápido que JS para operaciones criptográficas' },
                                { icon: <Lock size={16} />, title: 'Sandbox Seguro', desc: 'Ejecuta en sandbox del browser sin acceso directo a DOM o APIs sensibles' },
                                { icon: <Cpu size={16} />, title: 'Portable', desc: 'Mismo binario funciona en todos los browsers modernos sin modificación' },
                            ].map((v, i) => (
                                <div key={i} className="flex items-start gap-3 p-3 bg-black/20 rounded-lg">
                                    <div className="text-neon-blue mt-0.5">{v.icon}</div>
                                    <div>
                                        <span className="text-white text-sm font-medium">{v.title}</span>
                                        <p className="text-gray-500 text-xs mt-0.5">{v.desc}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Card>
                </div>
            )}

            {tab === 'roadmap' && (
                <div className="space-y-4">
                    {[
                        { phase: '3A', title: 'Prototipo HMAC', status: 'next', items: ['Compilar hmac_verifier.wasm desde Rust', 'Bridge TypeScript con wasm-bindgen', 'Benchmark vs crypto.subtle'] },
                        { phase: '3B', title: 'Validador de Licencias', status: 'planned', items: ['Portar lógica de validate_license a Rust', 'Compilar con wasm-pack', 'Integrar en vendor_core.ts'] },
                        { phase: '3C', title: 'Motor de Ofuscación', status: 'planned', items: ['Implementar control flow flattening en C', 'String encryption/decryption en runtime', 'Compilar con Emscripten'] },
                        { phase: '3D', title: 'Integrity Checker', status: 'future', items: ['Hash SHA-256 del bundle en build time', 'Verificación en runtime contra hash esperado', 'Alerta si código fue modificado'] },
                    ].map((p, i) => (
                        <Card key={i}>
                            <div className="flex items-center gap-3 mb-3">
                                <span className={`px-2 py-1 rounded text-xs font-mono font-bold ${p.status === 'next' ? 'bg-neon-blue/10 text-neon-blue border border-neon-blue/20' : p.status === 'planned' ? 'bg-yellow-500/10 text-yellow-400 border border-yellow-500/20' : 'bg-white/5 text-gray-500 border border-white/10'}`}>
                                    Fase {p.phase}
                                </span>
                                <span className="text-white font-bold text-sm">{p.title}</span>
                            </div>
                            <ul className="space-y-1.5">
                                {p.items.map((item, j) => (
                                    <li key={j} className="flex items-start gap-2 text-sm text-gray-400">
                                        <span className="w-1.5 h-1.5 rounded-full bg-gray-600 mt-1.5 shrink-0" />
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </Card>
                    ))}
                </div>
            )}
        </div>
    )
}
